// src/data/geographycontent3.js
// Grade 9 Ready — Geography Content Section 3
// Section Three: Rocks, Weathering and Soil
//
// Source basis:
// - Grade 9 Ready geography source material: Section Three — Rocks, Weathering and Soil
// - National Curriculum KS3 Geography: physical geography including rocks, weathering and soils
// - AQA GCSE Geography 8035: physical landscapes and weathering processes

export const geographyContent3 = [
  {
    id: "geography-section-3",
    sectionNumber: 3,
    slug: "rocks-weathering-and-soil",
    title: "Section Three — Rocks, Weathering and Soil",
    subtitle: "Rock types, the rock cycle, weathering processes and soil formation",
    examBoardLinks: [
      "KS3 Geography — rocks, weathering and soils",
      "AQA GCSE Geography 8035 — physical landscapes and weathering processes"
    ],
    overview:
      "This section explains how rocks are formed, how they are changed by weathering, and how soils develop. It introduces igneous, sedimentary and metamorphic rocks, then explores physical, biological and chemical weathering. The section builds the foundation for GCSE topics on coasts, rivers, glaciation, ecosystems and landscape change.",
    bigIdeas: [
      "Rocks are grouped into igneous, sedimentary and metamorphic rocks according to how they form.",
      "The rock cycle shows how rocks can change from one type to another over long periods of time.",
      "Weathering is the breakdown of rock in place, not the movement of rock material.",
      "Physical weathering breaks rock apart without changing its chemical composition.",
      "Chemical weathering changes the minerals within rock.",
      "Biological weathering is caused by plants, animals and other living organisms.",
      "Soil forms slowly from weathered rock, organic matter, water, air and living organisms.",
      "Rock type, climate, vegetation and slope all affect how quickly landscapes and soils change."
    ],
    keywords: [
      "rock",
      "mineral",
      "igneous rock",
      "sedimentary rock",
      "metamorphic rock",
      "magma",
      "lava",
      "cooling",
      "crystals",
      "intrusive rock",
      "extrusive rock",
      "granite",
      "basalt",
      "sediment",
      "compaction",
      "cementation",
      "strata",
      "layers",
      "fossils",
      "limestone",
      "sandstone",
      "chalk",
      "heat",
      "pressure",
      "marble",
      "slate",
      "quartzite",
      "rock cycle",
      "weathering",
      "physical weathering",
      "freeze-thaw weathering",
      "onion-skin weathering",
      "exfoliation",
      "biological weathering",
      "chemical weathering",
      "carbonation",
      "solution",
      "limestone pavement",
      "soil",
      "humus",
      "organic matter",
      "soil profile",
      "topsoil",
      "subsoil",
      "parent rock",
      "bedrock",
      "erosion",
      "transportation",
      "deposition",
      "granite landscape",
      "Dartmoor",
      "tor",
      "limestone landscape",
      "Yorkshire Dales",
      "Malham Cove",
      "clints",
      "grikes",
      "chalk landscape",
      "South Downs",
      "escarpment",
      "scarp slope",
      "dip slope",
      "quarrying",
      "land-use conflict"
    ],
    topics: [
      {
        id: "geo-3-1-types-of-rock",
        slug: "types-of-rock",
        title: "Types of Rock",
        unit: "Rocks, Weathering and Soil",
        learningObjectives: [
          "Identify the three main rock groups.",
          "Explain how igneous, sedimentary and metamorphic rocks form.",
          "Recognise common examples of each rock type."
        ],
        coreKnowledge: [
          {
            heading: "The three main rock groups",
            points: [
              "Rocks are usually grouped into three main types: igneous, sedimentary and metamorphic.",
              "Each rock type forms in a different way.",
              "The way a rock forms affects its appearance, strength, structure and resistance to weathering."
            ]
          },
          {
            heading: "Igneous rocks",
            points: [
              "Igneous rocks form when molten rock cools and solidifies.",
              "Molten rock below the surface is called magma.",
              "Molten rock at the surface is called lava.",
              "Intrusive igneous rocks form when magma cools slowly underground.",
              "Extrusive igneous rocks form when lava cools quickly at the surface.",
              "Granite and basalt are common examples of igneous rocks."
            ]
          },
          {
            heading: "Sedimentary rocks",
            points: [
              "Sedimentary rocks form from layers of sediment.",
              "Sediment may include fragments of rock, shells, sand, mud and the remains of living things.",
              "Over time, layers are compacted and cemented together.",
              "Sedimentary rocks often contain visible layers called strata.",
              "They may contain fossils because remains of plants and animals can be trapped in sediment.",
              "Limestone, sandstone and chalk are common examples of sedimentary rocks."
            ]
          },
          {
            heading: "Metamorphic rocks",
            points: [
              "Metamorphic rocks form when existing rocks are changed by heat and pressure.",
              "They do not melt completely; if they melted, they would become magma.",
              "Metamorphic rocks are often harder than the original rock.",
              "Limestone can become marble.",
              "Clay can become slate.",
              "Sandstone can become quartzite."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Explain how an igneous rock forms.",
            answer:
              "An igneous rock forms when molten rock cools and solidifies. If magma cools slowly underground, large crystals may form. If lava cools quickly at the surface, smaller crystals usually form."
          },
          {
            question: "Why do sedimentary rocks often contain fossils?",
            answer:
              "Sedimentary rocks often contain fossils because they form from layers of sediment. The remains of plants and animals can become buried in these layers and preserved as the sediment is compacted and cemented over time."
          },
          {
            question: "How does metamorphic rock form?",
            answer:
              "Metamorphic rock forms when an existing rock is changed by heat and pressure. The rock does not fully melt, but its minerals and structure are altered, often making it harder or more compact."
          }
        ],
        misconceptions: [
          "Lava and magma are not exactly the same: magma is underground, lava is at the surface.",
          "Metamorphic rock is not made by melting rock completely.",
          "Sedimentary rocks are not always soft, but they are often more layered and may be less resistant than some igneous or metamorphic rocks.",
          "Fossils are usually found in sedimentary rocks, not igneous rocks."
        ],
        quickCheck: [
          {
            question: "Which rock type forms when molten rock cools?",
            options: ["Igneous", "Sedimentary", "Metamorphic", "Soil"],
            answer: "Igneous",
            explanation:
              "Igneous rocks form when magma or lava cools and solidifies."
          },
          {
            question: "Which rock type is most likely to contain fossils?",
            options: ["Igneous", "Sedimentary", "Metamorphic", "Molten"],
            answer: "Sedimentary",
            explanation:
              "Fossils are most commonly found in sedimentary rocks because remains can be buried in layers of sediment."
          },
          {
            question: "Which process forms metamorphic rock?",
            options: [
              "Cooling of lava",
              "Heat and pressure changing existing rock",
              "Cloud condensation",
              "River deposition only"
            ],
            answer: "Heat and pressure changing existing rock",
            explanation:
              "Metamorphic rocks form when existing rocks are changed by heat and pressure."
          }
        ],
        grade9Move:
          "Link rock formation to rock properties. For example, sedimentary rocks often have layers and joints, which can make them more vulnerable to weathering and erosion."
      },
      {
        id: "geo-3-2-igneous-rocks",
        slug: "igneous-rocks",
        title: "Igneous Rocks",
        unit: "Rocks, Weathering and Soil",
        learningObjectives: [
          "Explain the difference between intrusive and extrusive igneous rocks.",
          "Understand how cooling speed affects crystal size.",
          "Use examples such as granite and basalt accurately."
        ],
        coreKnowledge: [
          {
            heading: "Intrusive igneous rocks",
            points: [
              "Intrusive igneous rocks form when magma cools underground.",
              "Because cooling is slow, crystals have more time to grow.",
              "This often produces rocks with larger crystals.",
              "Granite is a common intrusive igneous rock."
            ]
          },
          {
            heading: "Extrusive igneous rocks",
            points: [
              "Extrusive igneous rocks form when lava cools at or near the surface.",
              "Because cooling is rapid, crystals have less time to grow.",
              "This often produces rocks with smaller crystals.",
              "Basalt is a common extrusive igneous rock."
            ]
          },
          {
            heading: "Why crystal size matters",
            points: [
              "Large crystals usually suggest slow cooling underground.",
              "Small crystals usually suggest rapid cooling at the surface.",
              "Crystal size provides evidence about how and where an igneous rock formed."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Why does granite usually have larger crystals than basalt?",
            answer:
              "Granite usually has larger crystals because it forms underground from magma that cools slowly. Basalt usually forms from lava at the surface, where cooling is much faster, so crystals have less time to grow."
          },
          {
            question: "What is the difference between intrusive and extrusive igneous rock?",
            answer:
              "Intrusive igneous rock forms underground from slowly cooling magma. Extrusive igneous rock forms at or near the surface from quickly cooling lava."
          }
        ],
        misconceptions: [
          "Large crystals do not mean the rock is older; they usually mean it cooled slowly.",
          "Extrusive does not mean explosive. It means the rock formed from lava at or near the surface.",
          "Intrusive rocks form inside the Earth, not inside volcanoes only."
        ],
        quickCheck: [
          {
            question: "Which rock is commonly intrusive?",
            options: ["Granite", "Basalt", "Chalk", "Slate"],
            answer: "Granite",
            explanation:
              "Granite forms slowly underground and commonly has larger crystals."
          },
          {
            question: "Which rock is commonly extrusive?",
            options: ["Basalt", "Marble", "Limestone", "Quartzite"],
            answer: "Basalt",
            explanation:
              "Basalt commonly forms when lava cools quickly at the surface."
          },
          {
            question: "What does slow cooling usually produce?",
            options: [
              "Larger crystals",
              "No crystals ever",
              "Fossils",
              "Soil horizons"
            ],
            answer: "Larger crystals",
            explanation:
              "Slow cooling gives crystals more time to grow."
          }
        ],
        grade9Move:
          "Use evidence language: large crystals suggest slow underground cooling, while small crystals suggest rapid surface cooling."
      },
      {
        id: "geo-3-3-sedimentary-rocks",
        slug: "sedimentary-rocks",
        title: "Sedimentary Rocks",
        unit: "Rocks, Weathering and Soil",
        learningObjectives: [
          "Explain how sedimentary rocks form.",
          "Understand compaction and cementation.",
          "Recognise why sedimentary rocks often have layers and fossils."
        ],
        coreKnowledge: [
          {
            heading: "Sediment",
            points: [
              "Sediment is loose material such as sand, mud, shells or rock fragments.",
              "Sediment can be transported by rivers, wind, ice or the sea.",
              "When sediment is deposited, it can build up in layers."
            ]
          },
          {
            heading: "Compaction and cementation",
            points: [
              "As more layers build up, the weight presses lower layers together.",
              "This is called compaction.",
              "Minerals can bind the sediment together.",
              "This is called cementation.",
              "Over long periods, compacted and cemented sediment becomes sedimentary rock."
            ]
          },
          {
            heading: "Layers and fossils",
            points: [
              "Sedimentary rocks often have visible layers called strata.",
              "Fossils may be preserved when remains are buried in sediment.",
              "Limestone can form from shells and marine organisms.",
              "Sandstone forms mainly from compacted sand grains."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Explain how sandstone forms.",
            answer:
              "Sandstone forms when sand is deposited in layers. Over time, more sediment builds up above it. The lower layers are compacted by pressure and minerals cement the grains together, forming solid rock."
          },
          {
            question: "Why are sedimentary rocks useful evidence of past environments?",
            answer:
              "Sedimentary rocks can contain fossils, layers and sediment types that show what an environment was like in the past. For example, marine fossils in limestone can suggest the area was once covered by shallow sea."
          }
        ],
        misconceptions: [
          "Sedimentary rock is not made instantly; it forms over long periods.",
          "Compaction means squeezing sediment together; cementation means minerals bind it.",
          "Layers in sedimentary rock are called strata.",
          "Fossils are evidence, not decoration."
        ],
        quickCheck: [
          {
            question: "What is compaction?",
            options: [
              "Sediment being squeezed by pressure",
              "Lava cooling quickly",
              "Rock melting completely",
              "Roots breaking rock apart"
            ],
            answer: "Sediment being squeezed by pressure",
            explanation:
              "Compaction occurs when layers of sediment are pressed together by the weight above."
          },
          {
            question: "What is cementation?",
            options: [
              "Minerals binding sediment together",
              "Ice expanding in cracks",
              "Magma rising",
              "Plants adding humus"
            ],
            answer: "Minerals binding sediment together",
            explanation:
              "Cementation happens when minerals glue sediment grains together."
          },
          {
            question: "What are strata?",
            options: [
              "Rock layers",
              "Volcanic gases",
              "Soil animals",
              "Earthquake waves"
            ],
            answer: "Rock layers",
            explanation:
              "Strata are layers of sedimentary rock."
          }
        ],
        grade9Move:
          "When explaining sedimentary rock, use the full sequence: weathering → erosion → transportation → deposition → compaction → cementation."
      },
      {
        id: "geo-3-4-metamorphic-rocks",
        slug: "metamorphic-rocks",
        title: "Metamorphic Rocks",
        unit: "Rocks, Weathering and Soil",
        learningObjectives: [
          "Explain how metamorphic rocks form.",
          "Understand that metamorphism involves change without complete melting.",
          "Give examples of rocks changed by heat and pressure."
        ],
        coreKnowledge: [
          {
            heading: "Metamorphism",
            points: [
              "Metamorphic rocks form when existing rocks are changed by heat and pressure.",
              "This usually happens deep underground.",
              "The rock remains solid but its minerals and structure are changed.",
              "Metamorphic rocks are often harder and more resistant than the original rock."
            ]
          },
          {
            heading: "Examples",
            points: [
              "Limestone can be changed into marble.",
              "Clay can be changed into slate.",
              "Sandstone can be changed into quartzite.",
              "These changes happen because of increased heat, pressure or both."
            ]
          },
          {
            heading: "Metamorphic rocks in landscapes",
            points: [
              "Harder metamorphic rocks may resist weathering and erosion.",
              "This can influence upland areas, cliffs and steep slopes.",
              "Rock resistance helps explain why some landscapes are more rugged than others."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Why is marble described as a metamorphic rock?",
            answer:
              "Marble is described as a metamorphic rock because it forms when limestone is changed by heat and pressure. The original rock does not fully melt, but its structure and minerals are altered."
          },
          {
            question: "Explain why metamorphic rocks can be resistant to erosion.",
            answer:
              "Metamorphic rocks can be resistant to erosion because heat and pressure often make them harder and more compact. This means they may break down more slowly than softer rocks."
          }
        ],
        misconceptions: [
          "Metamorphic rocks do not form by complete melting.",
          "Heat and pressure change existing rock; they do not create rock from nothing.",
          "Marble is changed limestone, not a type of igneous rock."
        ],
        quickCheck: [
          {
            question: "Which rock can limestone become after metamorphism?",
            options: ["Marble", "Basalt", "Chalk", "Clay"],
            answer: "Marble",
            explanation:
              "Limestone can become marble when changed by heat and pressure."
          },
          {
            question: "Which rock can clay become?",
            options: ["Slate", "Granite", "Basalt", "Sand"],
            answer: "Slate",
            explanation:
              "Clay can be changed into slate by heat and pressure."
          },
          {
            question: "What must not happen during metamorphism?",
            options: [
              "Complete melting",
              "Heat",
              "Pressure",
              "Mineral change"
            ],
            answer: "Complete melting",
            explanation:
              "If rock completely melts, it becomes magma and may later form igneous rock."
          }
        ],
        grade9Move:
          "Use precise language: metamorphic rock is altered in solid state by heat and pressure; it is not fully melted."
      },
      {
        id: "geo-3-5-rock-cycle",
        slug: "rock-cycle",
        title: "The Rock Cycle",
        unit: "Rocks, Weathering and Soil",
        learningObjectives: [
          "Describe the rock cycle as a system.",
          "Explain how one rock type can change into another.",
          "Use process vocabulary accurately."
        ],
        coreKnowledge: [
          {
            heading: "A cycle of change",
            points: [
              "The rock cycle shows how rocks can change from one type to another.",
              "These changes happen through processes such as cooling, weathering, erosion, deposition, compaction, cementation, heat, pressure and melting.",
              "The cycle operates over very long timescales."
            ]
          },
          {
            heading: "From igneous to sedimentary",
            points: [
              "Igneous rock can be broken down by weathering.",
              "The broken material can be transported and deposited as sediment.",
              "Compaction and cementation can turn sediment into sedimentary rock."
            ]
          },
          {
            heading: "From sedimentary to metamorphic",
            points: [
              "Sedimentary rock can be buried deep underground.",
              "Heat and pressure can change it into metamorphic rock.",
              "If it melts completely, it becomes magma."
            ]
          },
          {
            heading: "From magma to igneous",
            points: [
              "Magma can cool and solidify underground to form intrusive igneous rock.",
              "Lava can cool and solidify at the surface to form extrusive igneous rock."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Explain how an igneous rock could eventually become a sedimentary rock.",
            answer:
              "An igneous rock could be broken down by weathering. The fragments could then be eroded, transported and deposited. Over time, the sediment could be compacted and cemented to form sedimentary rock."
          },
          {
            question: "Explain how a sedimentary rock could become an igneous rock.",
            answer:
              "A sedimentary rock could be buried deep underground and eventually melt into magma. If the magma later cools and solidifies, it forms igneous rock."
          }
        ],
        misconceptions: [
          "The rock cycle does not always happen in one fixed order.",
          "A rock does not need to pass through every stage before changing.",
          "Weathering and erosion are different processes.",
          "Melting produces magma; cooling magma or lava produces igneous rock."
        ],
        quickCheck: [
          {
            question: "Which process turns sediment into sedimentary rock?",
            options: [
              "Compaction and cementation",
              "Melting and cooling",
              "Heat and pressure only",
              "Photosynthesis"
            ],
            answer: "Compaction and cementation",
            explanation:
              "Sedimentary rocks form when sediment is compacted and cemented."
          },
          {
            question: "Which process changes rock into magma?",
            options: ["Melting", "Deposition", "Cementation", "Freezing"],
            answer: "Melting",
            explanation:
              "When rock melts, it becomes magma."
          },
          {
            question: "Which process forms igneous rock from magma?",
            options: ["Cooling", "Compaction", "Carbonation", "Soil creep"],
            answer: "Cooling",
            explanation:
              "Igneous rocks form when magma or lava cools and solidifies."
          }
        ],
        grade9Move:
          "For rock-cycle explanations, write in process chains and use arrows mentally: weathering → erosion → transportation → deposition → compaction → cementation."
      },
      {
        id: "geo-3-6-physical-weathering",
        slug: "physical-weathering",
        title: "Physical Weathering",
        unit: "Rocks, Weathering and Soil",
        learningObjectives: [
          "Define physical weathering.",
          "Explain freeze-thaw weathering.",
          "Explain onion-skin weathering.",
          "Understand where physical weathering is likely to be most active."
        ],
        coreKnowledge: [
          {
            heading: "What physical weathering means",
            points: [
              "Physical weathering is the breakdown of rock into smaller pieces without changing the chemical composition of the rock.",
              "It happens in place, meaning the rock is broken down where it is.",
              "It is different from erosion, which involves movement of material."
            ]
          },
          {
            heading: "Freeze-thaw weathering",
            points: [
              "Freeze-thaw weathering happens when water enters cracks in rock.",
              "At night or in cold conditions, the water freezes and expands.",
              "The expanding ice puts pressure on the rock.",
              "Repeated freezing and thawing widens the crack.",
              "Eventually, pieces of rock break away."
            ]
          },
          {
            heading: "Onion-skin weathering",
            points: [
              "Onion-skin weathering is also called exfoliation.",
              "It often happens in hot desert areas where temperatures change greatly between day and night.",
              "During the day, rock surfaces heat up and expand.",
              "At night, they cool and contract.",
              "Repeated expansion and contraction can cause outer layers to peel away."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Explain freeze-thaw weathering.",
            answer:
              "Freeze-thaw weathering happens when water enters a crack in rock. When the temperature falls below freezing, the water turns to ice and expands. This puts pressure on the crack. Repeated freezing and thawing widens the crack until pieces of rock break off."
          },
          {
            question: "Why is onion-skin weathering common in hot deserts?",
            answer:
              "Onion-skin weathering is common in hot deserts because there is a large temperature range between day and night. Rock expands when heated during the day and contracts when cooled at night. Repeated expansion and contraction causes outer layers to peel away."
          }
        ],
        misconceptions: [
          "Physical weathering does not change the chemical composition of rock.",
          "Freeze-thaw weathering needs repeated freezing and thawing, not just one cold night.",
          "Onion-skin weathering is linked to temperature changes, not rain.",
          "Weathering happens in place; erosion moves material away."
        ],
        quickCheck: [
          {
            question: "What is physical weathering?",
            options: [
              "Rock breaking down without chemical change",
              "Rock being transported by a river",
              "Rock dissolving in acid rain only",
              "Soil being deposited by wind"
            ],
            answer: "Rock breaking down without chemical change",
            explanation:
              "Physical weathering breaks rock apart but does not chemically alter it."
          },
          {
            question: "What causes freeze-thaw weathering?",
            options: [
              "Water freezing and expanding in cracks",
              "Tree roots taking nutrients",
              "Carbon dioxide dissolving in rainwater",
              "Sediment being deposited"
            ],
            answer: "Water freezing and expanding in cracks",
            explanation:
              "Freeze-thaw weathering occurs when water freezes, expands and puts pressure on rock cracks."
          },
          {
            question: "Onion-skin weathering is mainly caused by...",
            options: [
              "Repeated heating and cooling",
              "Animal burrowing only",
              "River deposition",
              "Earthquake waves"
            ],
            answer: "Repeated heating and cooling",
            explanation:
              "Heating causes expansion and cooling causes contraction, which can peel away outer rock layers."
          }
        ],
        grade9Move:
          "Use sequence language in process questions: water enters → freezes → expands → pressure increases → crack widens → rock breaks."
      },
      {
        id: "geo-3-7-biological-weathering",
        slug: "biological-weathering",
        title: "Biological Weathering",
        unit: "Rocks, Weathering and Soil",
        learningObjectives: [
          "Define biological weathering.",
          "Explain how plant roots break rock apart.",
          "Explain how animals and organisms contribute to weathering."
        ],
        coreKnowledge: [
          {
            heading: "What biological weathering means",
            points: [
              "Biological weathering is the breakdown of rock by living organisms.",
              "It can be caused by plants, animals, bacteria, fungi and lichens.",
              "It often works alongside physical and chemical weathering."
            ]
          },
          {
            heading: "Plant roots",
            points: [
              "Seeds can settle in cracks in rock.",
              "As plants grow, their roots expand.",
              "The roots push against the sides of the crack.",
              "Over time, this can widen cracks and break rock apart."
            ]
          },
          {
            heading: "Animals and organisms",
            points: [
              "Burrowing animals can loosen rock and soil.",
              "Worms and insects mix and break down soil material.",
              "Some organisms produce weak acids that help break down rock surfaces."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Explain how plant roots cause biological weathering.",
            answer:
              "Plant roots can grow into cracks in rocks. As the roots grow thicker, they push against the rock. This widens the crack and can eventually split pieces of rock away."
          },
          {
            question: "How can animals contribute to weathering?",
            answer:
              "Animals can contribute to weathering by burrowing into soil and loose rock. This exposes more rock surfaces to air and water and can help break up material."
          }
        ],
        misconceptions: [
          "Biological weathering is not only caused by large tree roots.",
          "Small organisms such as lichens and bacteria can also contribute to weathering.",
          "Biological weathering can be both physical and chemical in effect."
        ],
        quickCheck: [
          {
            question: "Which is an example of biological weathering?",
            options: [
              "Tree roots widening cracks",
              "Ice expanding in cracks",
              "Lava cooling",
              "Sediment being deposited"
            ],
            answer: "Tree roots widening cracks",
            explanation:
              "Plant roots are living organisms, so this is biological weathering."
          },
          {
            question: "What can burrowing animals do?",
            options: [
              "Loosen rock and soil",
              "Create magma",
              "Stop all erosion",
              "Turn lava into fossils"
            ],
            answer: "Loosen rock and soil",
            explanation:
              "Burrowing animals can break up and loosen material."
          }
        ],
        grade9Move:
          "For higher-level answers, explain how biological weathering increases surface area, allowing other weathering processes to act more quickly."
      },
      {
        id: "geo-3-8-chemical-weathering",
        slug: "chemical-weathering",
        title: "Chemical Weathering",
        unit: "Rocks, Weathering and Soil",
        learningObjectives: [
          "Define chemical weathering.",
          "Explain carbonation and solution.",
          "Understand why limestone is vulnerable to chemical weathering."
        ],
        coreKnowledge: [
          {
            heading: "What chemical weathering means",
            points: [
              "Chemical weathering breaks down rock by changing its minerals.",
              "It usually involves chemical reactions between rock, water, oxygen or weak acids.",
              "Chemical weathering is often faster in warm, wet climates."
            ]
          },
          {
            heading: "Carbonation",
            points: [
              "Rainwater absorbs carbon dioxide from the air.",
              "This forms weak carbonic acid.",
              "Carbonic acid reacts with calcium carbonate in limestone.",
              "This slowly dissolves the limestone.",
              "Over time, this can create features such as widened joints, caves and limestone pavements."
            ]
          },
          {
            heading: "Solution",
            points: [
              "Solution happens when minerals dissolve in water.",
              "Some rocks and minerals are more soluble than others.",
              "Limestone is especially vulnerable because it reacts with weakly acidic rainwater."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Explain carbonation weathering in limestone.",
            answer:
              "Carbonation happens when rainwater absorbs carbon dioxide and forms weak carbonic acid. This reacts with calcium carbonate in limestone and slowly dissolves it. Over time, cracks and joints can widen and limestone features can develop."
          },
          {
            question: "Why is chemical weathering often faster in warm, wet climates?",
            answer:
              "Chemical weathering is often faster in warm, wet climates because water is needed for many chemical reactions and warmer temperatures can speed up the rate of reaction."
          }
        ],
        misconceptions: [
          "Chemical weathering changes the rock chemically; it is not just breaking it into smaller pieces.",
          "Carbonation is not the same as carbonated drinks, although both involve carbon dioxide.",
          "Limestone is vulnerable because it contains calcium carbonate.",
          "Chemical weathering can happen slowly but still create major landscape features over time."
        ],
        quickCheck: [
          {
            question: "What does chemical weathering do?",
            options: [
              "Changes the minerals in rock",
              "Only moves sediment downstream",
              "Only freezes water",
              "Creates tectonic plates"
            ],
            answer: "Changes the minerals in rock",
            explanation:
              "Chemical weathering involves chemical changes to minerals in the rock."
          },
          {
            question: "Which rock is especially affected by carbonation?",
            options: ["Limestone", "Granite", "Basalt", "Slate"],
            answer: "Limestone",
            explanation:
              "Limestone contains calcium carbonate, which reacts with weak carbonic acid."
          },
          {
            question: "What weak acid is involved in carbonation?",
            options: [
              "Carbonic acid",
              "Sulfuric lava",
              "Frozen acid",
              "Humus acid only"
            ],
            answer: "Carbonic acid",
            explanation:
              "Rainwater absorbs carbon dioxide to form weak carbonic acid."
          }
        ],
        grade9Move:
          "Use chemistry carefully: rainwater + carbon dioxide → weak carbonic acid; carbonic acid reacts with calcium carbonate in limestone."
      },
      {
        id: "geo-3-9-soil-formation",
        slug: "soil-formation",
        title: "Soil Formation",
        unit: "Rocks, Weathering and Soil",
        learningObjectives: [
          "Explain what soil is made from.",
          "Describe how soil forms.",
          "Understand the main layers of a soil profile."
        ],
        coreKnowledge: [
          {
            heading: "What soil is",
            points: [
              "Soil is a mixture of mineral particles, organic matter, water and air.",
              "Mineral particles come from weathered rock.",
              "Organic matter comes from dead plants and animals.",
              "Humus is dark, nutrient-rich organic matter."
            ]
          },
          {
            heading: "How soil forms",
            points: [
              "Rock is broken down by weathering.",
              "Small mineral particles mix with decaying organic matter.",
              "Water, air and living organisms help develop the soil.",
              "Soil formation is slow and can take hundreds or thousands of years."
            ]
          },
          {
            heading: "Soil profile",
            points: [
              "A soil profile is a vertical section through soil.",
              "Topsoil is usually rich in humus and living organisms.",
              "Subsoil contains more mineral particles and less organic matter.",
              "Parent material is partly weathered rock.",
              "Bedrock is the solid rock beneath the soil."
            ]
          },
          {
            heading: "Factors affecting soil",
            points: [
              "Climate affects weathering, decomposition and leaching.",
              "Vegetation affects the amount of organic matter added to soil.",
              "Rock type affects mineral content and drainage.",
              "Relief affects soil depth because steep slopes often have thinner soils.",
              "Time affects how developed the soil profile becomes."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Explain how soil forms.",
            answer:
              "Soil forms when rock is broken down by weathering into mineral particles. These particles mix with decaying organic matter from plants and animals. Over time, water, air and organisms help create layers of soil."
          },
          {
            question: "Why is topsoil important?",
            answer:
              "Topsoil is important because it usually contains humus, nutrients, water and many living organisms. This makes it important for plant growth and farming."
          },
          {
            question: "Why might soil be thinner on a steep slope?",
            answer:
              "Soil may be thinner on a steep slope because gravity and runoff can move weathered material downslope. This means soil has less time to build up in one place."
          }
        ],
        misconceptions: [
          "Soil is not just dirt; it is a system containing minerals, organic matter, water, air and organisms.",
          "Soil does not form quickly.",
          "Humus is not the same as hummus; humus is decayed organic matter in soil.",
          "Bedrock is not soil, but it provides parent material when weathered."
        ],
        quickCheck: [
          {
            question: "What is humus?",
            options: [
              "Decayed organic matter",
              "Molten rock",
              "A type of earthquake wave",
              "A volcanic gas"
            ],
            answer: "Decayed organic matter",
            explanation:
              "Humus forms from decayed plant and animal material."
          },
          {
            question: "Which soil layer usually contains the most organic matter?",
            options: ["Topsoil", "Bedrock", "Core", "Lava layer"],
            answer: "Topsoil",
            explanation:
              "Topsoil usually contains the most humus and living organisms."
          },
          {
            question: "Which factor can affect soil formation?",
            options: [
              "Climate",
              "Vegetation",
              "Rock type",
              "All of these"
            ],
            answer: "All of these",
            explanation:
              "Climate, vegetation and rock type all influence soil formation."
          }
        ],
        grade9Move:
          "Connect soil formation to systems thinking: rock type, climate, vegetation, relief, organisms and time interact to create different soils."
      },
      {
        id: "geo-3-10-weathering-erosion-and-landscape-change",
        slug: "weathering-erosion-and-landscape-change",
        title: "Weathering, Erosion and Landscape Change",
        unit: "Rocks, Weathering and Soil",
        learningObjectives: [
          "Distinguish between weathering and erosion.",
          "Explain how weathering prepares material for erosion.",
          "Understand why rock type affects landscape shape."
        ],
        coreKnowledge: [
          {
            heading: "Weathering versus erosion",
            points: [
              "Weathering is the breakdown of rock in place.",
              "Erosion is the wearing away and removal of material by moving forces such as rivers, waves, glaciers or wind.",
              "Transportation is the movement of eroded material.",
              "Deposition happens when transported material is dropped."
            ]
          },
          {
            heading: "How weathering supports erosion",
            points: [
              "Weathering weakens rock and breaks it into smaller pieces.",
              "This makes it easier for rivers, waves, ice or wind to erode and transport material.",
              "Weathering and erosion often work together to shape landscapes."
            ]
          },
          {
            heading: "Rock type and landscape",
            points: [
              "Harder, more resistant rocks often form uplands, cliffs or steep slopes.",
              "Softer, less resistant rocks are worn away more easily and may form lower land or bays.",
              "Joints, cracks, layers and faults can create lines of weakness.",
              "Water, roots and ice can exploit these weaknesses."
            ]
          }
        ],
        workedExamples: [
          {
            question: "What is the difference between weathering and erosion?",
            answer:
              "Weathering is the breakdown of rock in place. Erosion is the removal of rock or sediment by moving forces such as water, waves, ice or wind."
          },
          {
            question: "How can rock type influence a coastline?",
            answer:
              "Harder rocks are more resistant to erosion and may form headlands. Softer rocks are eroded more quickly and may form bays. This creates an uneven coastline."
          },
          {
            question: "Why does weathering make erosion easier?",
            answer:
              "Weathering breaks and weakens rock, creating smaller pieces and cracks. This gives rivers, waves, glaciers or wind more material to remove and transport."
          }
        ],
        misconceptions: [
          "Weathering and erosion are not the same.",
          "Weathering happens in place; erosion involves movement.",
          "Deposition is not erosion; it is the dropping of transported material.",
          "Hard rock can still be weathered and eroded, but usually more slowly."
        ],
        quickCheck: [
          {
            question: "Which statement is correct?",
            options: [
              "Weathering breaks rock down in place",
              "Weathering always moves rock downstream",
              "Erosion never involves movement",
              "Deposition means rock melting"
            ],
            answer: "Weathering breaks rock down in place",
            explanation:
              "Weathering is the breakdown of rock where it is."
          },
          {
            question: "What is erosion?",
            options: [
              "The wearing away and removal of material",
              "The formation of magma",
              "The growth of plant roots only",
              "The creation of humus only"
            ],
            answer: "The wearing away and removal of material",
            explanation:
              "Erosion removes material using moving forces such as water, wind, waves or ice."
          },
          {
            question: "What usually happens when transported material is dropped?",
            options: ["Deposition", "Subduction", "Metamorphism", "Cooling"],
            answer: "Deposition",
            explanation:
              "Deposition is the laying down or dropping of material."
          }
        ],
        grade9Move:
          "In GCSE landscape answers, combine processes: weathering weakens rock, erosion removes it, transportation moves it, and deposition creates new landforms."
      },
      {
        id: "geo-3-11-rocks-landscapes-and-people",
        slug: "rocks-landscapes-and-people",
        title: "Rocks, Landscapes and People",
        unit: "Rocks, Weathering and Soil",
        learningObjectives: [
          "Describe how granite, limestone and chalk form distinctive UK landscapes.",
          "Explain how rock type influences land use and human activity.",
          "Use UK case studies (Dartmoor, Yorkshire Dales, South Downs) to support answers.",
          "Evaluate the conflicts caused by quarrying in upland rock landscapes."
        ],
        coreKnowledge: [
          {
            heading: "Granite landscapes — Dartmoor, Devon",
            points: [
              "Granite is an intrusive igneous rock that cools slowly underground and forms large interlocking crystals.",
              "Dartmoor is an exposed granite batholith in south-west England, lifted up over millions of years.",
              "The landscape is bleak, treeless moorland with rocky outcrops called tors (e.g. Haytor, Hound Tor).",
              "Granite is hard, impermeable and resists chemical weathering, so the land is high, with thin acidic soils.",
              "Land use is limited — rough sheep grazing, military training (Okehampton firing range), tourism, china clay extraction at Lee Moor.",
              "Granite weathers slowly through freeze-thaw along its joints, creating tor-like blocky outcrops."
            ]
          },
          {
            heading: "Limestone landscapes — Yorkshire Dales",
            points: [
              "Carboniferous limestone is a hard, jointed sedimentary rock made mainly of calcium carbonate.",
              "The Yorkshire Dales, including Malham in North Yorkshire, are a classic UK limestone landscape.",
              "Surface features include limestone pavements (clints and grikes), dry valleys, gorges and scars (e.g. Malham Cove).",
              "Underground features include caves, swallow holes, stalactites and stalagmites — formed by carbonation as weak carbonic acid dissolves calcium carbonate.",
              "Limestone is permeable along joints, so streams often disappear underground.",
              "Land use includes sheep farming, dairy on lower slopes, tourism (walking, caving), and quarrying."
            ]
          },
          {
            heading: "Chalk landscapes — South Downs",
            points: [
              "Chalk is a soft, white, very pure sedimentary rock made of microscopic marine fossils.",
              "The South Downs in southern England form a chalk escarpment running through Hampshire and Sussex.",
              "Chalk is permeable but soft — it forms rounded hills, dry valleys and steep scarp slopes with gentler dip slopes.",
              "Where chalk meets the sea, it forms striking white cliffs (e.g. Seven Sisters, Beachy Head).",
              "Chalk holds groundwater in aquifers — important for water supply in south-east England.",
              "Land use includes arable farming on dip slopes, sheep grazing on steeper land, and the South Downs National Park for recreation."
            ]
          },
          {
            heading: "Quarrying — a conflict of land use",
            points: [
              "Granite, limestone and chalk are all quarried for construction, cement, road stone and agriculture.",
              "Quarrying brings jobs and income to remote rural areas, and supplies vital raw materials.",
              "However, it creates visual scars, noise, dust, heavy lorry traffic and damage to ecosystems.",
              "Many quarries are inside or close to National Parks (e.g. Yorkshire Dales, Peak District), which causes conflict between local economies and conservation aims.",
              "Restoration after quarrying — landscaping, replanting and nature reserves — can reduce long-term damage."
            ]
          }
        ],
        workedExamples: [
          {
            question:
              "Explain how granite influences the landscape and land use of Dartmoor.",
            answer:
              "Dartmoor is an exposed granite batholith. Granite is hard, impermeable and resistant to chemical weathering, so it forms high, bleak moorland with rocky tors such as Haytor. Soils are thin and acidic, so the main land use is rough sheep grazing, with smaller areas used for china clay extraction, military training and tourism."
          },
          {
            question:
              "Describe two surface features found in the limestone landscape of the Yorkshire Dales.",
            answer:
              "Limestone pavements made of flat blocks (clints) separated by deepened joints (grikes) are common, for example at Malham. Dry valleys and steep scars such as Malham Cove also occur where former rivers have left the land or where the rock has been exposed by glaciation."
          },
          {
            question:
              "Why does quarrying often cause conflict in upland rock landscapes?",
            answer:
              "Quarrying creates jobs, supplies stone for construction and brings income to rural areas. However, it damages scenery, creates dust, noise and lorry traffic, and harms ecosystems. Because many quarries are inside or near National Parks like the Yorkshire Dales, there is tension between people who want economic activity and those who want to conserve the landscape."
          }
        ],
        misconceptions: [
          "Granite is not 'just any hard rock' — it is a specific intrusive igneous rock with visible crystals.",
          "Limestone landscapes are not always flat; they include pavements, gorges, scars and underground caves.",
          "Chalk is not the same as limestone — it is softer and made almost entirely of tiny marine fossils.",
          "Quarrying is not always bad: restored quarries can become nature reserves or recreation sites."
        ],
        quickCheck: [
          {
            question: "Which UK upland is a classic example of a granite landscape?",
            options: ["Dartmoor", "Yorkshire Dales", "South Downs", "Norfolk Broads"],
            answer: "Dartmoor",
            explanation:
              "Dartmoor is an exposed granite batholith with tors and bleak moorland."
          },
          {
            question:
              "What is a limestone pavement made of clints and grikes most associated with?",
            options: [
              "Yorkshire Dales",
              "Dartmoor",
              "South Downs",
              "The Lake District granite"
            ],
            answer: "Yorkshire Dales",
            explanation:
              "Limestone pavements such as those above Malham Cove in the Yorkshire Dales form when carbonation widens joints in carboniferous limestone."
          },
          {
            question: "Which rock type forms the rolling hills of the South Downs?",
            options: ["Chalk", "Granite", "Basalt", "Slate"],
            answer: "Chalk",
            explanation:
              "The South Downs are a chalk escarpment with rounded hills, dry valleys and white sea cliffs."
          },
          {
            question:
              "Which is a typical land-use conflict caused by quarrying in a National Park?",
            options: [
              "Lorry traffic, dust and noise versus conservation and tourism",
              "More fishing versus more farming",
              "More volcanoes versus more earthquakes",
              "More magma versus more lava"
            ],
            answer:
              "Lorry traffic, dust and noise versus conservation and tourism",
            explanation:
              "Quarries bring jobs and stone but damage landscapes, ecosystems and the visitor experience that National Parks try to protect."
          },
          {
            question:
              "Why does the South Downs chalk landscape support arable farming on its dip slopes?",
            options: [
              "Gentle slopes, free-draining soils and a relatively dry climate",
              "Permanently frozen ground",
              "Volcanic ash soils",
              "Mountain glaciers"
            ],
            answer:
              "Gentle slopes, free-draining soils and a relatively dry climate",
            explanation:
              "Chalk dip slopes are gentle and well drained, which suits cereal crops in southern England."
          }
        ],
        grade9Move:
          "Top answers explicitly link rock properties (hardness, permeability, jointing) to landscape features (tors, pavements, scarps) and then to human activity (sheep grazing, arable farming, quarrying, tourism). Always quote a specific UK location — Dartmoor, Malham, South Downs — to anchor the case study."
      }
    ],
    sectionPractice: {
      warmUpQuestions: [
        {
          question: "Name the three main types of rock.",
          suggestedAnswer: "Igneous, sedimentary and metamorphic."
        },
        {
          question: "What is the difference between magma and lava?",
          suggestedAnswer:
            "Magma is molten rock below the surface; lava is molten rock at the surface."
        },
        {
          question: "What type of rock often contains fossils?",
          suggestedAnswer: "Sedimentary rock."
        },
        {
          question: "What is freeze-thaw weathering?",
          suggestedAnswer:
            "Water enters cracks, freezes, expands and eventually breaks rock apart after repeated cycles."
        },
        {
          question: "What is humus?",
          suggestedAnswer: "Decayed organic matter in soil."
        }
      ],
      examStyleQuestions: [
        {
          marks: 2,
          command: "Define",
          question: "Define the term weathering.",
          indicativeContent:
            "Weathering is the breakdown of rock in place by physical, chemical or biological processes."
        },
        {
          marks: 3,
          command: "Describe",
          question: "Describe two characteristics of sedimentary rocks.",
          indicativeContent:
            "They often have visible layers or strata; they may contain fossils; they form from compacted and cemented sediment."
        },
        {
          marks: 4,
          command: "Explain",
          question: "Explain how freeze-thaw weathering breaks down rock.",
          indicativeContent:
            "Water enters cracks; temperature falls below freezing; water freezes and expands; pressure widens the crack; repeated freeze-thaw cycles eventually cause pieces of rock to break away."
        },
        {
          marks: 4,
          command: "Explain",
          question: "Explain how chemical weathering affects limestone.",
          indicativeContent:
            "Rainwater absorbs carbon dioxide and becomes weak carbonic acid; this reacts with calcium carbonate in limestone; the limestone slowly dissolves; joints and cracks may widen over time."
        },
        {
          marks: 6,
          command: "Explain",
          question:
            "Explain how rock type and weathering processes can influence the shape of a landscape.",
          indicativeContent:
            "Harder rocks resist weathering and erosion and may form uplands, steep slopes or headlands. Softer or jointed rocks break down more easily. Physical, chemical and biological weathering weaken rock, allowing erosion to remove material and shape features such as cliffs, valleys, bays or limestone landscapes."
        },
        {
          marks: 9,
          command: "Assess",
          question:
            "Assess the importance of weathering in shaping physical landscapes.",
          indicativeContent:
            "Weathering is important because it weakens and breaks down rock, creating material for erosion and influencing slope, cliff and soil development. Physical weathering is important in cold or hot climates; chemical weathering is important in warm, wet climates and limestone areas; biological weathering contributes through roots and organisms. However, erosion, transportation, deposition, rock structure, climate and human activity also shape landscapes. Best answers make a balanced judgement."
        },
        {
          marks: 6,
          command: "Explain",
          question:
            "Using a named UK example, explain how rock type influences both landscape and land use.",
          indicativeContent:
            "Award marks for accurate use of a UK case study (Dartmoor granite, Yorkshire Dales limestone or South Downs chalk). Strong answers link rock properties (hardness, permeability, jointing) to landforms (tors, limestone pavements, dry valleys, scarp/dip slopes) and then to human activity (sheep grazing on thin acidic granite soils, arable farming on chalk dip slopes, tourism and quarrying in limestone areas)."
        }
      ],
      objectiveQuestions: [
        {
          question: "Which rock type forms from cooled magma or lava?",
          options: ["Igneous", "Sedimentary", "Metamorphic", "Organic"],
          answer: "Igneous"
        },
        {
          question: "Which rock type forms from compacted and cemented sediment?",
          options: ["Igneous", "Sedimentary", "Metamorphic", "Volcanic"],
          answer: "Sedimentary"
        },
        {
          question: "Which rock type forms from heat and pressure?",
          options: ["Igneous", "Sedimentary", "Metamorphic", "Alluvial"],
          answer: "Metamorphic"
        },
        {
          question: "Which is an example of an intrusive igneous rock?",
          options: ["Granite", "Basalt", "Chalk", "Slate"],
          answer: "Granite"
        },
        {
          question: "Which is an example of a metamorphic rock?",
          options: ["Slate", "Sand", "Lava", "Clay soil"],
          answer: "Slate"
        },
        {
          question: "What is freeze-thaw weathering?",
          options: [
            "Water freezes and expands in rock cracks",
            "Rock dissolves in carbonic acid",
            "Sediment is compacted into layers",
            "Lava cools quickly"
          ],
          answer: "Water freezes and expands in rock cracks"
        },
        {
          question: "Onion-skin weathering is linked to...",
          options: [
            "Repeated heating and cooling",
            "Only plant roots",
            "Compaction and cementation",
            "Subduction"
          ],
          answer: "Repeated heating and cooling"
        },
        {
          question: "Which is biological weathering?",
          options: [
            "Tree roots widening cracks",
            "Limestone dissolving",
            "Magma cooling",
            "Sediment being deposited"
          ],
          answer: "Tree roots widening cracks"
        },
        {
          question: "Which rock is especially vulnerable to carbonation?",
          options: ["Limestone", "Granite", "Basalt", "Slate"],
          answer: "Limestone"
        },
        {
          question: "What is topsoil usually rich in?",
          options: ["Humus", "Magma", "Lava", "Seismic waves"],
          answer: "Humus"
        },
        {
          question: "Which UK upland is a granite landscape with tors?",
          options: ["Dartmoor", "South Downs", "Yorkshire Wolds", "The Fens"],
          answer: "Dartmoor"
        },
        {
          question:
            "Limestone pavements with clints and grikes are best linked to which UK region?",
          options: [
            "Yorkshire Dales (Malham)",
            "South Downs",
            "Dartmoor",
            "Norfolk Broads"
          ],
          answer: "Yorkshire Dales (Malham)"
        },
        {
          question: "The South Downs are made mainly of which rock?",
          options: ["Chalk", "Granite", "Slate", "Basalt"],
          answer: "Chalk"
        }
      ],
      aiChallenge: {
        title: "Grade 9 Landscape Process Explanation",
        prompt:
          "A coastal cliff is made from layers of limestone with many cracks and joints. Explain how weathering could weaken the cliff over time. In your answer, refer to physical, chemical and biological weathering, and explain how this could make erosion more effective.",
        successCriteria: [
          "Clearly define weathering as breakdown of rock in place.",
          "Explain freeze-thaw or another physical weathering process in sequence.",
          "Explain carbonation/solution in limestone using carbonic acid and calcium carbonate.",
          "Explain biological weathering using roots or organisms.",
          "Link weathering to erosion by explaining that weakened rock is easier to remove.",
          "Use accurate geographical terminology throughout."
        ]
      }
    },
    revisionSummary: [
      "There are three main rock types: igneous, sedimentary and metamorphic.",
      "Igneous rocks form when magma or lava cools and solidifies.",
      "Intrusive igneous rocks cool slowly underground and often have larger crystals.",
      "Extrusive igneous rocks cool quickly at the surface and often have smaller crystals.",
      "Sedimentary rocks form from compacted and cemented sediment and often contain layers and fossils.",
      "Metamorphic rocks form when existing rocks are changed by heat and pressure without fully melting.",
      "The rock cycle shows how rocks can change through weathering, erosion, deposition, compaction, cementation, heat, pressure, melting and cooling.",
      "Weathering is the breakdown of rock in place.",
      "Physical weathering breaks rock apart without chemical change.",
      "Freeze-thaw weathering happens when water freezes, expands and widens cracks.",
      "Onion-skin weathering happens when repeated heating and cooling causes outer rock layers to peel away.",
      "Biological weathering is caused by living organisms such as roots, burrowing animals and lichens.",
      "Chemical weathering changes rock minerals; carbonation is important in limestone areas.",
      "Soil forms from weathered rock, organic matter, water, air and living organisms.",
      "Weathering weakens rock, making erosion more effective in shaping landscapes.",
      "Granite forms tors and bleak moorland — for example Dartmoor — with thin acidic soils and limited land use (sheep grazing, china clay, tourism).",
      "Carboniferous limestone forms pavements, scars and caves — for example the Yorkshire Dales around Malham — and supports tourism, sheep farming and quarrying.",
      "Chalk forms gentle escarpments with scarp and dip slopes — for example the South Downs — and supports arable farming, sheep grazing and aquifers for water supply.",
      "Quarrying brings jobs and stone but causes land-use conflict in National Parks because of dust, noise, lorry traffic and damage to ecosystems."
    ]
  }
];

export default geographyContent3;